import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { Document } from '@/types/Document';
import type { Signee } from '@/types/Signee.ts';
import { SigneeType } from '@/types/enums/SigneeType.ts';
import AuthService from '@/services/authService.ts';
import { useLocalInviteesDataActions } from '@/components/create/composables/useLocalInviteesDataActions.ts';
import { useInviteesSignaturePlacement } from '@/components/create/composables/useInviteesSignaturePlacement.ts';
import { useCreateStoreHelpers } from '@/components/create/composables/useCreateStoreHelpers.ts';
import { useInviteesActions } from '@/components/create/composables/useInviteesActions.ts';
import { useParticipantsReconcile } from '@/components/create/composables/useParticipantsReconcile.ts';
import type { CompanySettings } from '@/types/deepsign/CompanySettings';
import { useApi } from '@/composables/useApi.ts';
import {
  getDefaultSigneeFileId,
  hasSigneeAutographPositions,
  isSigneeGroupQuorumSet,
  persistSigneeGroupSignatureFields,
  resolveSigneeSignings
} from '@/utils/signeeSignings.ts';
import { updateSigneeGroup } from '@/api/deepsign/documents-signee-groups.ts';
import type { SigneeGroup } from '@/types/deepsign/SigneeGroup.ts';
import type { SigneeGroupUpdate } from '@/types/deepsign/SigneeGroupUpdate.ts';
import type { AutographPosition } from '@/types/AutographPosition.ts';

// One signee's snapshotted state right before a sign-order update: their
// group's quorum, and — for quorum groups only — the per-file autograph
// positions their shared slot held. See `setQuorumSnapshot`.
type QuorumSnapshotEntry = {
  requiredSignatures: number | null;
  requiredApprovals: number | null;
  positionsByFile: Record<string, AutographPosition[]>;
};
type QuorumSnapshot = Map<string, QuorumSnapshotEntry>;

export const useCreateStore = defineStore('createStore', () => {
  const { deepSignAPI } = useApi();
  //STATE
  const document = ref<Document | undefined>();
  const ordered = ref(false);
  const hasOrderBeenSetManually = ref(false);
  const isLoading = ref(false);
  const placeSignaturesModel = ref(false);
  // True while confirm() is auto-placing the signees it just created. In that
  // window the participants are already committed but their placeholders are
  // not persisted yet, so per-file completeness reads false — PlaceSignaturesSwitch
  // holds its ON state (and `placeSignaturesModel`) instead of blinking OFF and
  // back ON behind the closing dialog.
  const isAutoPlacingNewSignees = ref(false);
  // When true (signing-workflow dialog open), participant/sign-order actions
  // mutate local state only and skip the backend — changes are persisted in a
  // single batch by reconcileParticipants() when the user clicks "Confirm".
  const deferPersistence = ref(false);
  const companySettings = ref<CompanySettings>();
  const hasNoPaymentMethodAndFreeSes = ref(false);
  const fetchingAccountingSettings = ref();
  // Working set for the signing-workflow dialog. While the dialog is open
  // (deferPersistence === true) the editing UI mutates this draft clone and the
  // real document.signeesOrdered stays untouched until Confirm. See
  // useParticipantsReconcile for the begin/discard/commit lifecycle.
  const draftSigneesOrdered = ref<Array<Array<Signee>> | null>(null);
  // Pending quorum (requiredSignatures / requiredApprovals) edits made in the
  // signing-workflow dialog, keyed by the group's signOrder (its position).
  // Persisted on the dialog's Confirm (reconcileGroupQuorums); cleared on
  // open/reset. The groups don't have server signeeGroupIds until then.
  const draftGroupQuorums = ref<Map<number, number | null>>(new Map());
  // Snapshot of each signee's group quorum (requiredSignatures /
  // requiredApprovals) and — for quorum groups — the shared slot placement
  // (per-file autograph positions) they occupied, keyed by signeeId, taken
  // right before a sign-order update is sent (see useParticipantsReconcile).
  // The backend recreates signee groups whenever the grouping/order changes,
  // which drops any previously-set quorum AND the group's placed slot
  // placeholders back to null/empty, even for groups the user never touched
  // — reconcileGroupQuorums() uses this snapshot afterwards to restore both
  // on groups whose membership didn't change.
  const quorumSnapshotBySigneeId = ref<QuorumSnapshot | null>(null);

  function setQuorumSnapshot(signeeGroups?: SigneeGroup[] | null) {
    const snapshot: QuorumSnapshot = new Map();
    for (const group of signeeGroups ?? []) {
      const isQuorumGroup = isSigneeGroupQuorumSet(group);
      group.signees?.forEach((signee, index) => {
        const positionsByFile: Record<string, AutographPosition[]> = {};
        if (isQuorumGroup) {
          for (const signing of group.signings ?? []) {
            if (
              signing.signatureSlot === index &&
              signing.autographPositions.length > 0
            ) {
              positionsByFile[signing.fileId] = signing.autographPositions;
            }
          }
        }
        snapshot.set(signee.signeeId, {
          requiredSignatures: group.requiredSignatures ?? null,
          requiredApprovals: group.requiredApprovals ?? null,
          positionsByFile
        });
      });
    }
    quorumSnapshotBySigneeId.value = snapshot;
  }

  // The participant editing surface reads and writes through this working set:
  // the draft while the dialog defers persistence, the document otherwise.
  const signeesOrdered = computed<Array<Array<Signee>>>({
    get: () =>
      deferPersistence.value && draftSigneesOrdered.value
        ? draftSigneesOrdered.value
        : document.value?.signeesOrdered || [],
    set: (groups) => {
      if (deferPersistence.value) {
        // While the dialog defers persistence every write targets the draft —
        // creating it if a broken open sequence left it missing — so dialog
        // edits can never leak into the committed document.
        draftSigneesOrdered.value = groups;
      } else if (document.value) {
        document.value.signeesOrdered = groups;
      }
    }
  });
  const signeesOrderedFlat = computed(() => signeesOrdered.value.flat());
  // Committed sign-order groups straight from the document — never reflects the
  // dialog's in-progress draft. Used by the drawer summary.
  const committedSigneesOrdered = computed(
    () => document.value?.signeesOrdered || []
  );
  // Committed participant views — the counterparts of `signeesOrderedFlat` /
  // `filteredSignees` / `hasInvitees` that NEVER see the dialog's draft. Every
  // surface OUTSIDE the signing-workflow dialog (the PDF canvas and its
  // placeholders, the sidebar, the drawer's summary and switches) must read
  // these, so an edit made in the dialog only becomes visible there once
  // Confirm commits it into the document. While the dialog is closed they are
  // identical to their draft-aware counterparts.
  const committedSigneesFlat = computed(() =>
    committedSigneesOrdered.value.flat()
  );
  const committedSignees = computed(() =>
    committedSigneesFlat.value.filter(
      (signee) => signee.signeeType === SigneeType.SIGN
    )
  );
  const committedHasInvitees = computed(
    () => committedSigneesFlat.value.length > 0
  );
  const filteredSignees = computed(() =>
    signeesOrderedFlat.value.filter(
      (signee) => signee.signeeType === SigneeType.SIGN
    )
  );
  const filteredApprovers = computed(() =>
    signeesOrderedFlat.value.filter(
      (signee) => signee.signeeType === SigneeType.APPROVE
    )
  );
  const placedSignatures = computed(() => {
    if (filteredSignees.value) {
      return filteredSignees.value.filter((signee) =>
        hasSigneeAutographPositions(signee)
      );
    }
    return [];
  });
  const nextGroupIndex = computed(() => {
    if (signeesOrdered.value.length === 0 || !ordered.value) {
      return 0;
    }
    return signeesOrdered.value.length || 0;
  });

  const hasInvitees = computed(() => signeesOrderedFlat.value.length > 0);
  const hasSignees = computed(() => {
    const signees = signeesOrderedFlat.value.filter(
      (signee) => signee.signeeType === SigneeType.SIGN
    );
    return signees.length > 0;
  });

  const hasApprovers = computed(() => {
    const approvers = signeesOrderedFlat.value.filter(
      (signee) => signee.signeeType === SigneeType.APPROVE
    );
    return approvers.length > 0;
  });

  const hasOnlyEmptyInvitees = computed(
    () =>
      signeesOrderedFlat.value.every(
        (signee) => signee.email === null || signee.email?.trim().length === 0
      ) || false
  );

  const initiatorAsSignee = computed(() => {
    return filteredSignees.value?.find((s) =>
      compareString(s.email as string, AuthService.user?.email)
    );
  });

  const hasEmptyObservers = computed(() => {
    return document.value?.observers.some((o) => o.email === null) || false;
  });

  const hasEmptySignees = computed(() => {
    return (
      signeesOrderedFlat.value.some(
        (invitee: Signee) =>
          invitee.email === null && invitee.signeeType === SigneeType.SIGN
      ) || false
    );
  });

  const hasEmptyApprovers = computed(() => {
    return (
      signeesOrderedFlat.value.some(
        (invitee: Signee) =>
          invitee.email === null && invitee.signeeType === SigneeType.APPROVE
      ) || false
    );
  });

  const isEnvelope = computed(() => (document.value?.files?.length ?? 0) > 1);

  const inviteeFormTemplateRef = ref<HTMLDivElement | null>(null);

  const { compareString, changeEmailAddress, focusInputField, validateEmail } =
    useCreateStoreHelpers();
  const {
    addInvitee,
    addObserver,
    deleteObserver,
    createUpdateSignOrderPayload,
    createDocumentFromDeepBox,
    createSealDocumentFromFile,
    createDocumentFromFile,
    enforceAuthorityService,
    getSignOrder,
    updateDocumentById,
    updateSignatureMessage,
    updateAttachmentsAllow,
    updateAuthorityService,
    updateDocumentObserver,
    updateSignatureLevel,
    updateInvitee,
    removeInvitee,
    removeAllInviteeFieldsWithEmptyEmail,
    removeAllObserversWithEmptyEmail
  } = useInviteesActions(useCreateStore());
  const {
    addDropZones,
    addInviteeToLocalData,
    clearInvalidFields,
    createInitialGroupsWithApprovers,
    createUnorderedGroup,
    filterEmptyInBetweenGroups,
    filterInviteeFromLocalData,
    updateSignOrderInLocalData,
    updateInviteeInLocalData,
    removeInviteeFromLocalData,
    setSigneeAttachmentsAllowed,
    setEnforceAuthorityService
  } = useLocalInviteesDataActions(useCreateStore());
  const { removeSignature } = useInviteesSignaturePlacement(useCreateStore());
  const {
    beginParticipantsDraft,
    discardParticipantsDraft,
    isParticipantsDraftDiscardable,
    reconcileParticipants
  } = useParticipantsReconcile(useCreateStore());

  //ACTIONS
  async function getDocument() {
    return document.value || null;
  }

  function setDocument(doc: Document) {
    document.value = doc;
  }

  //HELPERS
  function resetStore() {
    document.value = undefined;
    ordered.value = false;
    isLoading.value = false;
    placeSignaturesModel.value = false;
    isAutoPlacingNewSignees.value = false;
    deferPersistence.value = false;
    draftSigneesOrdered.value = null;
    clearGroupQuorumDraft();
  }

  // Maps a signeesOrdered group to its envelope signee group via member id
  // (signeeId is stable; signeesOrdered itself doesn't carry signeeGroupId).
  function findSigneeGroupForGroup(
    group: Array<Signee>
  ): SigneeGroup | undefined {
    const memberIds = new Set(group.map((signee) => signee.signeeId));
    return document.value?.signeeGroups?.find((signeeGroup) =>
      signeeGroup.signees?.some((signee) => memberIds.has(signee.signeeId))
    );
  }

  // The group's quorum: the pending draft edit if one exists, otherwise the
  // value persisted on the document's signee group. SIGN groups use
  // requiredSignatures, APPROVE groups requiredApprovals.
  function getGroupQuorum(group: Array<Signee>): number | null {
    if (group.length === 0) return null;
    const order = group[0]?.signOrder ?? 0;
    if (draftGroupQuorums.value.has(order)) {
      return draftGroupQuorums.value.get(order) ?? null;
    }
    const signeeGroup = findSigneeGroupForGroup(group);
    if (!signeeGroup) return null;
    const isApproverGroup = group[0]?.signeeType === SigneeType.APPROVE;
    return (
      (isApproverGroup
        ? signeeGroup.requiredApprovals
        : signeeGroup.requiredSignatures) ?? null
    );
  }

  // True once any (non-empty) sign-order group — including the single
  // ungrouped bucket used when the sign order is off — already carries a
  // quorum. Drives the "Gruppensignierung" toggle's disabled state: the user
  // must remove every quorum first before the controls can be hidden again.
  const hasActiveGroupQuorum = computed(() =>
    signeesOrdered.value.some(
      (group) => group.length > 0 && getGroupQuorum(group) !== null
    )
  );

  // Records a quorum edit in the draft only — no API call. It's persisted on the
  // dialog's Confirm by reconcileGroupQuorums(), once the groups exist
  // server-side with real signeeGroupIds. Keyed by the group's signOrder.
  function setGroupQuorum(group: Array<Signee>, value: number | null) {
    if (group.length === 0) return;
    draftGroupQuorums.value.set(group[0]?.signOrder ?? 0, value);
  }

  // Drops the pending quorum edits while KEEPING the pre-sign-order snapshot
  // (the restore pass in reconcileGroupQuorums still needs it). Used when the
  // grouping is restructured wholesale: the edits are keyed by signOrder, and
  // after a restructure that key identifies a different group — e.g. an
  // ungrouped quorum recorded at order 0 would land on the approver group that
  // adding an approver inserts at order 0.
  function clearGroupQuorumDraftEdits() {
    draftGroupQuorums.value = new Map();
  }

  function clearGroupQuorumDraft() {
    clearGroupQuorumDraftEdits();
    quorumSnapshotBySigneeId.value = null;
  }

  // Removing a quorum makes the backend drop every signing of the group: the
  // shared slots disappear and the members do NOT get individual positions
  // back. Mirror that locally, otherwise the members' stale `signings` /
  // `autographPosition` resurface as phantom per-signee placeholders the
  // moment `isSigneeGroupQuorumSet` stops routing placement lookups through
  // the group's slots. Clears the group's own signings plus every member's
  // placement data in all three places it is mirrored: the group, the
  // committed `signeesOrdered` and the dialog's draft.
  function clearGroupPlacementsLocally(signeeGroup: SigneeGroup) {
    signeeGroup.signings = [];
    const memberIds = new Set(
      (signeeGroup.signees ?? []).map((signee) => signee.signeeId)
    );
    if (memberIds.size === 0) return;

    function clearPlacement(signee: Signee) {
      signee.autographPosition = null;
      signee.signings = [];
    }
    function clearMatchingMembers(signees?: Signee[]) {
      signees?.forEach((signee) => {
        if (memberIds.has(signee.signeeId)) clearPlacement(signee);
      });
    }

    document.value?.signeeGroups?.forEach((group) =>
      clearMatchingMembers(group.signees)
    );
    document.value?.signeesOrdered?.forEach(clearMatchingMembers);
    draftSigneesOrdered.value?.forEach(clearMatchingMembers);
  }

  // A quorum only makes sense for a group of 2+ members. Clears (PUTs null)
  // requiredSignatures/requiredApprovals on a group that has shrunk to a
  // single signee/approver, e.g. after removing a group member. Returns true
  // if it handled the group (so the caller can skip further processing).
  async function clearQuorumIfSingleMember(
    documentId: string,
    signeeGroup: SigneeGroup
  ): Promise<boolean> {
    if ((signeeGroup.signees?.length ?? 0) > 1) return false;
    if (!signeeGroup.signeeGroupId) return true;
    const isApproverGroup =
      signeeGroup.signees?.[0]?.signeeType === SigneeType.APPROVE;
    const currentValue = isApproverGroup
      ? signeeGroup.requiredApprovals
      : signeeGroup.requiredSignatures;
    if (currentValue == null) return true;

    const payload: SigneeGroupUpdate = isApproverGroup
      ? { requiredApprovals: null }
      : { requiredSignatures: null };
    try {
      await updateSigneeGroup(documentId, signeeGroup.signeeGroupId, payload);
      if (isApproverGroup) signeeGroup.requiredApprovals = null;
      else {
        signeeGroup.requiredSignatures = null;
        clearGroupPlacementsLocally(signeeGroup);
      }
    } catch (error) {
      console.error('Failed to reset single-member signee group quorum', error);
    }
    return true;
  }

  // Persists one group's explicit quorum edit made in this dialog session —
  // as-is, including an explicit "remove" (null). A single-member group can
  // never carry a quorum, regardless of the edited value.
  async function persistExplicitGroupQuorum(
    documentId: string,
    signeeGroups: SigneeGroup[],
    order: number,
    value: number | null
  ) {
    const signeeGroup = signeeGroups.find((sg) => sg.signOrder === order);
    if (!signeeGroup?.signeeGroupId) return;
    if (await clearQuorumIfSingleMember(documentId, signeeGroup)) return;
    const isApproverGroup =
      signeeGroup.signees?.[0]?.signeeType === SigneeType.APPROVE;
    const current =
      (isApproverGroup
        ? signeeGroup.requiredApprovals
        : signeeGroup.requiredSignatures) ?? null;
    if (current === value) return;
    const payload: SigneeGroupUpdate = isApproverGroup
      ? { requiredApprovals: value }
      : { requiredSignatures: value };
    try {
      await updateSigneeGroup(documentId, signeeGroup.signeeGroupId, payload);
      if (isApproverGroup) signeeGroup.requiredApprovals = value;
      else {
        const hadQuorum = isSigneeGroupQuorumSet(signeeGroup);
        signeeGroup.requiredSignatures = value;
        // Dropping the quorum wipes the group's slots server-side.
        if (hadQuorum && !value) clearGroupPlacementsLocally(signeeGroup);
      }
    } catch (error) {
      console.error('Failed to persist signee group quorum', error);
    }
  }

  // The quorum (requiredSignatures/requiredApprovals) that the majority of a
  // group's previously-known members agreed on, or null if there's no clear
  // majority or none of the members are recognized from the snapshot. Compares
  // only the quorum values, not the snapshotted slot positions (those are
  // per-member and would never form a majority).
  function majorityQuorum(
    signeeGroup: SigneeGroup,
    snapshot: QuorumSnapshot
  ): {
    requiredSignatures: number | null;
    requiredApprovals: number | null;
  } | null {
    const knownMembers = (signeeGroup.signees ?? [])
      .map((signee) => snapshot.get(signee.signeeId))
      .filter((quorum): quorum is NonNullable<typeof quorum> => !!quorum)
      .map(({ requiredSignatures, requiredApprovals }) => ({
        requiredSignatures,
        requiredApprovals
      }));
    if (knownMembers.length === 0) return null;

    const counts = new Map<string, number>();
    for (const quorum of knownMembers) {
      const key = JSON.stringify(quorum);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    let bestKey: string | null = null;
    let bestCount = 0;
    for (const [key, count] of counts) {
      if (count > bestCount) {
        bestKey = key;
        bestCount = count;
      }
    }
    if (!bestKey || bestCount / knownMembers.length <= 0.5) return null;
    return JSON.parse(bestKey);
  }

  // Restores a group's quorum that existed before this confirm but was wiped
  // by the backend recreating signee groups when the grouping/sign order
  // changed (e.g. adding/removing a group or a signee elsewhere). Skipped for
  // groups the user explicitly edited this session (handled above already).
  // A group that shrank to a single member never gets its quorum restored —
  // it's cleared instead, since a quorum makes no sense for one signee.
  async function restoreGroupQuorumIfLost(
    documentId: string,
    signeeGroup: SigneeGroup,
    snapshot: QuorumSnapshot
  ) {
    if (!signeeGroup.signeeGroupId) return;
    if (await clearQuorumIfSingleMember(documentId, signeeGroup)) return;

    const previousQuorum = majorityQuorum(signeeGroup, snapshot);
    if (!previousQuorum) return;

    const isApproverGroup =
      signeeGroup.signees?.[0]?.signeeType === SigneeType.APPROVE;
    const desiredValue = isApproverGroup
      ? previousQuorum.requiredApprovals
      : previousQuorum.requiredSignatures;
    if (!desiredValue) return;
    const currentValue = isApproverGroup
      ? signeeGroup.requiredApprovals
      : signeeGroup.requiredSignatures;
    if (currentValue === desiredValue) return;

    const payload: SigneeGroupUpdate = isApproverGroup
      ? { requiredApprovals: desiredValue }
      : { requiredSignatures: desiredValue };
    try {
      await updateSigneeGroup(documentId, signeeGroup.signeeGroupId, payload);
      if (isApproverGroup) signeeGroup.requiredApprovals = desiredValue;
      else signeeGroup.requiredSignatures = desiredValue;
    } catch (error) {
      console.error('Failed to restore signee group quorum', error);
    }
  }

  // Restores a quorum group's shared slot placements (placed autograph
  // positions) that were wiped by the same backend group recreation that
  // drops the quorum value. Only applies to groups that (still/again) have a
  // quorum set — non-quorum signees own their positions directly and never
  // lose them this way. For each member with a snapshotted position whose
  // current slot reads empty, re-persists it via the signee-group signature
  // slot endpoint at the member's (possibly new) slot index, so an already
  // placed placeholder doesn't silently disappear from the sidebar/canvas
  // just because a group member was added or removed elsewhere.
  async function restoreGroupSlotPlacementsIfLost(
    documentId: string,
    signeeGroup: SigneeGroup,
    snapshot: QuorumSnapshot
  ) {
    if (!signeeGroup.signeeGroupId) return;
    if (!isSigneeGroupQuorumSet(signeeGroup)) return;

    for (const signee of signeeGroup.signees ?? []) {
      const knownPositions = snapshot.get(signee.signeeId)?.positionsByFile;
      if (!knownPositions || Object.keys(knownPositions).length === 0) continue;

      const slotIndex = signeeGroup.signees?.findIndex(
        (candidate) => candidate.signeeId === signee.signeeId
      );
      const missingPositions: Record<string, AutographPosition[]> = {};
      for (const [fileId, positions] of Object.entries(knownPositions)) {
        const existing = signeeGroup.signings?.find(
          (signing) =>
            signing.fileId === fileId && signing.signatureSlot === slotIndex
        );
        if (!existing?.autographPositions?.length) {
          missingPositions[fileId] = positions;
        }
      }
      if (Object.keys(missingPositions).length === 0) continue;

      try {
        await persistSigneeGroupSignatureFields(
          documentId,
          signeeGroup,
          signee.signeeId,
          missingPositions
        );
      } catch (error) {
        console.error('Failed to restore signee group slot placement', error);
      }
    }
  }

  // Per-file autograph positions a single signee had placed, keyed by fileId.
  type MemberPlacements = Record<string, AutographPosition[]>;

  // Each member's own placements, in group (slot) order. Read BEFORE a quorum
  // is persisted: the backend replaces per-signee placements with empty shared
  // slots the moment a group becomes a quorum group, so there is no way to
  // recover them afterwards.
  function collectMemberPlacements(
    signeeGroup: SigneeGroup
  ): MemberPlacements[] {
    const defaultFileId = getDefaultSigneeFileId(document.value);
    return (signeeGroup.signees ?? []).map((signee) => {
      const placements: MemberPlacements = {};
      for (const signing of resolveSigneeSignings(signee, document.value)) {
        if (signing.autographPositions?.length) {
          placements[signing.fileId] = signing.autographPositions;
        }
      }
      // Legacy single-file documents only expose the document-wide position.
      if (
        Object.keys(placements).length === 0 &&
        signee.autographPosition &&
        defaultFileId
      ) {
        placements[defaultFileId] = [signee.autographPosition];
      }
      return placements;
    });
  }

  // Carries the placements a group's members owned individually into the
  // shared slots the backend creates when the group becomes a quorum group.
  // Setting a quorum wipes the per-signee positions and leaves every slot
  // empty, so the slot placeholder the UI renders right after Confirm has
  // nothing persisted behind it and vanishes on the next reload. For each slot
  // the group actually owns (`requiredSignatures`), reuse the next member
  // placement on that file, in group order — with a quorum of 1 that is simply
  // the first viable placeholder position.
  async function seedGroupSlotsFromMemberPlacements(
    documentId: string,
    signeeGroup: SigneeGroup,
    memberPlacements: MemberPlacements[]
  ) {
    if (!signeeGroup.signeeGroupId) return;
    if (!isSigneeGroupQuorumSet(signeeGroup)) return;

    // Per file, the placed positions in member order — members without a
    // placement on that file are skipped so slot 0 always gets the first
    // viable position rather than an empty one.
    const placedByFile = new Map<string, AutographPosition[][]>();
    for (const placements of memberPlacements) {
      for (const [fileId, positions] of Object.entries(placements)) {
        const positionsPerSlot = placedByFile.get(fileId) ?? [];
        positionsPerSlot.push(positions);
        placedByFile.set(fileId, positionsPerSlot);
      }
    }
    if (placedByFile.size === 0) return;

    const slotCount = Math.min(
      signeeGroup.requiredSignatures ?? 0,
      signeeGroup.signees?.length ?? 0
    );
    for (let slot = 0; slot < slotCount; slot++) {
      const signeeId = signeeGroup.signees?.[slot]?.signeeId;
      if (!signeeId) continue;

      const missingPositions: MemberPlacements = {};
      for (const [fileId, positionsPerSlot] of placedByFile) {
        const positions = positionsPerSlot[slot];
        if (!positions?.length) continue;
        // Never overwrite a slot the backend already filled.
        const existing = signeeGroup.signings?.find(
          (signing) =>
            signing.fileId === fileId && signing.signatureSlot === slot
        );
        if (existing?.autographPositions?.length) continue;
        missingPositions[fileId] = positions;
      }
      if (Object.keys(missingPositions).length === 0) continue;

      try {
        await persistSigneeGroupSignatureFields(
          documentId,
          signeeGroup,
          signeeId,
          missingPositions
        );
      } catch (error) {
        console.error(
          'Failed to carry member placements into signee group slots',
          error
        );
      }
    }
  }

  // A group's requiredSignatures/requiredApprovals can never exceed its
  // current member count. Removing a member from a quorum group doesn't
  // always wipe the quorum (unlike the grouping/order recreation
  // `restoreGroupQuorumIfLost` compensates for) — the backend can leave a
  // now-too-large value in place (e.g. requiredSignatures stays 3 after the
  // group shrinks to 2 members). Runs as a final safety net over every group
  // after explicit edits and snapshot restores, clamping the value down to
  // the new member count and persisting the change. A group that shrank to a
  // single member is handled separately by `clearQuorumIfSingleMember`.
  async function clampGroupQuorumToMembership(
    documentId: string,
    signeeGroup: SigneeGroup
  ) {
    if (!signeeGroup.signeeGroupId) return;
    const memberCount = signeeGroup.signees?.length ?? 0;
    if (memberCount <= 1) return;

    const isApproverGroup =
      signeeGroup.signees?.[0]?.signeeType === SigneeType.APPROVE;
    const currentValue = isApproverGroup
      ? signeeGroup.requiredApprovals
      : signeeGroup.requiredSignatures;
    if (!currentValue || currentValue <= memberCount) return;

    const payload: SigneeGroupUpdate = isApproverGroup
      ? { requiredApprovals: memberCount }
      : { requiredSignatures: memberCount };
    try {
      await updateSigneeGroup(documentId, signeeGroup.signeeGroupId, payload);
      if (isApproverGroup) signeeGroup.requiredApprovals = memberCount;
      else signeeGroup.requiredSignatures = memberCount;
    } catch (error) {
      console.error(
        'Failed to clamp signee group quorum to member count',
        error
      );
    }
  }

  // Clears a quorum on every group the eligibility rule doesn't allow one on
  // (the persistence-side mirror of `useQuorumEligibility`), so a quorum set
  // before the group became ineligible can't survive — e.g. a member's type
  // was toggled this session without touching the quorum control or the sign
  // order/grouping. `signeesOrdered` (not `signeeGroups`) is used to classify
  // the groups, since a plain signeeType edit doesn't refresh `signeeGroups`
  // and its cached member types would otherwise be stale.
  async function resetUnsupportedGroupQuorums() {
    const documentId = document.value?.documentId;
    if (!documentId) return;
    const groups = document.value?.signeesOrdered ?? [];
    // Without a sign order every invitee shares ONE signee group, so a single
    // approver anywhere in the list makes that shared group ineligible — not
    // only a group holding both types. With a real sequence the per-group
    // "mixed" rule applies instead: an all-approver step legitimately carries
    // requiredApprovals.
    const isUngrouped = groups.filter((group) => group.length > 0).length <= 1;
    for (const group of groups) {
      if (group.length < 2) continue;
      const hasApprover = group.some(
        (signee) => signee.signeeType === SigneeType.APPROVE
      );
      const hasSignee = group.some(
        (signee) => signee.signeeType === SigneeType.SIGN
      );
      const isEligible = isUngrouped
        ? !hasApprover
        : !hasApprover || !hasSignee;
      if (isEligible) continue;

      const signeeGroup = findSigneeGroupForGroup(group);
      if (!signeeGroup?.signeeGroupId) continue;
      if (
        signeeGroup.requiredSignatures == null &&
        signeeGroup.requiredApprovals == null
      ) {
        continue;
      }

      const hadQuorum = isSigneeGroupQuorumSet(signeeGroup);
      try {
        await updateSigneeGroup(documentId, signeeGroup.signeeGroupId, {
          requiredSignatures: null,
          requiredApprovals: null
        });
        signeeGroup.requiredSignatures = null;
        signeeGroup.requiredApprovals = null;
        if (hadQuorum) clearGroupPlacementsLocally(signeeGroup);
      } catch (error) {
        console.error('Failed to reset unsupported signee group quorum', error);
      }
    }
  }

  // Persists pending quorum edits. Called on dialog Confirm *after* participants
  // and sign order are reconciled, so the groups exist server-side: refresh
  // signeeGroups to learn their ids/signOrders, then PUT each changed quorum.
  //
  // `membershipChanged` reports whether this confirm created or deleted any
  // signee. Without a sign order no grouping call is ever made (see step 4 of
  // reconcileParticipants), so adding/removing an invitee leaves no explicit
  // edit and no quorum snapshot behind — and the clamp/clear passes below,
  // which keep requiredSignatures within the group's member count, would never
  // run for an ungrouped quorum. Membership alone has to arm them.
  async function reconcileGroupQuorums(membershipChanged = false) {
    const hasExplicitEdits = draftGroupQuorums.value.size > 0;
    const hasQuorumSnapshot = !!quorumSnapshotBySigneeId.value?.size;
    if (hasExplicitEdits || hasQuorumSnapshot || membershipChanged) {
      await reconcileNonMixedGroupQuorums();
    }
    // Runs last, after any snapshot restore above, so it overrides a quorum
    // that restore might have just reinstated onto a group that turned
    // ineligible within this same confirm. Always runs, unlike the block
    // above, since a signeeType-only edit doesn't set an explicit edit or a
    // quorum snapshot.
    await resetUnsupportedGroupQuorums();
  }

  async function reconcileNonMixedGroupQuorums() {
    await refreshSigneeGroups();
    const documentId = document.value?.documentId;
    const signeeGroups = document.value?.signeeGroups ?? [];
    if (documentId) {
      // Capture the members' own placements before any quorum is persisted —
      // the backend drops them as soon as the group turns into a quorum group.
      const placementsByGroupId = new Map<string, MemberPlacements[]>();
      for (const signeeGroup of signeeGroups) {
        if (signeeGroup.signeeGroupId && !isSigneeGroupQuorumSet(signeeGroup)) {
          placementsByGroupId.set(
            signeeGroup.signeeGroupId,
            collectMemberPlacements(signeeGroup)
          );
        }
      }

      for (const [order, value] of draftGroupQuorums.value) {
        await persistExplicitGroupQuorum(
          documentId,
          signeeGroups,
          order,
          value
        );
      }

      const explicitlyEditedOrders = new Set(draftGroupQuorums.value.keys());
      const snapshot = quorumSnapshotBySigneeId.value;
      if (snapshot) {
        for (const signeeGroup of signeeGroups) {
          if (!explicitlyEditedOrders.has(signeeGroup.signOrder)) {
            await restoreGroupQuorumIfLost(documentId, signeeGroup, snapshot);
          }
          await restoreGroupSlotPlacementsIfLost(
            documentId,
            signeeGroup,
            snapshot
          );
        }
      }

      // Final safety net: whatever the group's quorum ended up being above
      // (untouched, explicitly edited, or restored), it must still fit the
      // group's current member count — e.g. removing a member from a
      // 3-member/requiredSignatures:3 group leaves it at 2 members, so it
      // must clamp down to 2 rather than staying at 3.
      for (const signeeGroup of signeeGroups) {
        await clampGroupQuorumToMembership(documentId, signeeGroup);
      }

      // Runs last so the slot count reflects the group's final quorum: every
      // group that just gained one adopts its members' placements as the
      // shared slot positions, instead of starting out empty.
      for (const signeeGroup of signeeGroups) {
        const memberPlacements = signeeGroup.signeeGroupId
          ? placementsByGroupId.get(signeeGroup.signeeGroupId)
          : undefined;
        if (!memberPlacements) continue;
        await seedGroupSlotsFromMemberPlacements(
          documentId,
          signeeGroup,
          memberPlacements
        );
      }

      // Re-read the groups the quorum PUTs above reshaped server-side (slots
      // created, seeded or dropped). Besides keeping local state truthful this
      // replaces the `signeeGroups` array reference, which is what the
      // placeholder rebuild watch tracks — without it the canvas keeps
      // rendering the placeholders of a quorum that no longer exists.
      await refreshSigneeGroups();
    }
    clearGroupQuorumDraft();
    quorumSnapshotBySigneeId.value = null;
  }

  // Refreshes the server-owned slices a file mutation affects — files,
  // documentPolicy and signeeGroups — from one GET, without disturbing the rest
  // of the working draft. Used after add/delete so the envelope file list
  // reflects the server-assigned fileIds, thumbnails and order. The policy
  // depends on the file count (canModifyFileOrder only becomes true once the
  // document holds 2+ files) and the signee groups carry the per-file signings
  // the backend creates/removes with the file — the missing-signature badge and
  // per-file placement checks are blind to the change without them. Signee
  // groups hold no unsaved local state (participant edits are buffered in the
  // participants draft, placements persist immediately), so a wholesale replace
  // is safe.
  async function refreshDocumentFiles() {
    const documentId = document.value?.documentId;
    if (!documentId) return;
    try {
      const response = await deepSignAPI.value.documents.getById(documentId);
      const fresh = response.data as unknown as Document;
      if (document.value) {
        document.value.files = fresh.files;
        document.value.documentPolicy = fresh.documentPolicy;
        document.value.signeeGroups = fresh.signeeGroups;
      }
    } catch (error) {
      console.error('Failed to refresh document files', error);
    }
  }

  // Refreshes document.signeeGroups from the backend without disturbing the
  // working draft (only the signeeGroups field is replaced).
  async function refreshSigneeGroups() {
    const documentId = document.value?.documentId;
    if (!documentId) return;
    try {
      const response = await deepSignAPI.value.documents.getById(documentId);
      const fresh = (response.data as unknown as Document).signeeGroups;
      if (document.value) document.value.signeeGroups = fresh;
    } catch (error) {
      console.error('Failed to refresh signee groups', error);
    }
  }

  async function onOrderSwitchToggle(
    orderSwitchValue: boolean,
    isApproverCase: boolean = false,
    tempGroups: Array<Array<Signee>> | null,
    id?: string
  ) {
    // Local regrouping only: the ordered/unordered choice, like every other
    // participant edit in the signing-workflow dialog, is persisted in one
    // batch by reconcileParticipants() on Confirm.
    //
    // Every branch below rewrites the grouping wholesale, so no group survives
    // under the signOrder the pending quorum edits were recorded against —
    // drop them rather than let them re-target whichever group lands on that
    // order (see clearGroupQuorumDraftEdits). Toggling the sequence is an
    // explicit restructure, so losing a pending quorum edit with it is the
    // predictable outcome. The snapshot is kept: Confirm still needs it to
    // restore the quorums of groups the user never touched.
    clearGroupQuorumDraftEdits();
    if (!orderSwitchValue && !isApproverCase) {
      signeesOrdered.value = createUnorderedGroup(signeesOrdered.value);
    } else if (!isApproverCase) {
      signeesOrdered.value = signeesOrdered.value
        .flat()
        .map((signee: Signee, index) => {
          signee.signOrder = index;
          return [signee];
        });
    } else if (isApproverCase && tempGroups) {
      signeesOrdered.value = createInitialGroupsWithApprovers(tempGroups);
    }
    ordered.value = orderSwitchValue;
    if (id) {
      await focusInputField(id);
    }
  }

  async function getSettings(companyId: string) {
    if (companyId) {
      const settingsResponse =
        await deepSignAPI.value.documentsCompanySettings.getCompanySettings(
          companyId
        );
      companySettings.value = settingsResponse.data;
    } else {
      console.error('No CompanyID provided');
    }
  }

  return {
    //STATE
    document,
    isLoading,
    filteredSignees,
    filteredApprovers,
    hasApprovers,
    hasInvitees,
    hasSignees,
    hasEmptySignees,
    hasEmptyObservers,
    hasEmptyApprovers,
    hasOrderBeenSetManually,
    hasOnlyEmptyInvitees,
    nextGroupIndex,
    ordered,
    initiatorAsSignee,
    placedSignatures,
    placeSignaturesModel,
    isAutoPlacingNewSignees,
    deferPersistence,
    signeesOrdered,
    signeesOrderedFlat,
    committedSigneesOrdered,
    committedSignees,
    committedHasInvitees,
    draftSigneesOrdered,
    getGroupQuorum,
    setGroupQuorum,
    hasActiveGroupQuorum,
    setQuorumSnapshot,
    findSigneeGroupForGroup,
    reconcileGroupQuorums,
    clearGroupQuorumDraft,
    companySettings,
    inviteeFormTemplateRef,
    hasNoPaymentMethodAndFreeSes,
    fetchingAccountingSettings,
    isEnvelope,
    //ACTIONS TO BE
    addInvitee,
    addObserver,
    createDocumentFromDeepBox,
    createSealDocumentFromFile,
    createDocumentFromFile,
    deleteObserver,
    enforceAuthorityService,
    getSignOrder,
    updateDocumentById,
    updateDocumentObserver,
    updateSignatureMessage,
    updateAttachmentsAllow,
    updateAuthorityService,
    updateSignatureLevel,
    updateInvitee,
    removeInvitee,
    removeAllInviteeFieldsWithEmptyEmail,
    removeAllObserversWithEmptyEmail,
    getCompanySettings: getSettings,
    //LOCAL DATA ACTIONS
    addInviteeToLocalData,
    createInitialGroupsWithApprovers,
    createUnorderedGroup,
    removeInviteeFromLocalData,
    filterInviteeFromLocalData,
    updateSignOrderInLocalData,
    filterEmptyInBetweenGroups,
    updateInviteeInLocalData,
    setSigneeAttachmentsAllowed,
    setEnforceAuthorityService,
    //DEFERRED PERSISTENCE (signing-workflow dialog)
    beginParticipantsDraft,
    discardParticipantsDraft,
    isParticipantsDraftDiscardable,
    reconcileParticipants,
    //HELPERS
    onOrderSwitchToggle,
    clearInvalidFields,
    changeEmailAddress,
    addDropZones,
    focusInputField,
    validateEmail,
    createUpdateSignOrderPayload,
    resetStore,
    setDocument,
    getDocument,
    refreshDocumentFiles,
    refreshSigneeGroups,
    // SIGNATURE PLACEMENT ACTIONS
    removeSignature
  };
});
